import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, Settings, RefreshCw, CheckCircle, AlertCircle, XCircle, Clock,
  Database, CreditCard, Users, Tv, FileText, Globe, Calendar, Zap,
  Activity, Wifi, WifiOff, ChevronRight
} from 'lucide-react';
import { 
  allIntegrations, 
  regionalCalendars, 
  integrationCategories, 
  integrationStatuses, 
  integrationRegions 
} from '../data/integrationsData';

const Integrations = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: 'All Categories',
    status: 'All Status',
    region: 'All Regions'
  });
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Connected':
      case 'Active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Error':
      case 'Disconnected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'Pending Setup':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Connected': 'bg-green-100 text-green-800 hover:bg-green-200',
      'Active': 'bg-green-100 text-green-800 hover:bg-green-200',
      'Pending Setup': 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
      'Error': 'bg-red-100 text-red-800 hover:bg-red-200',
      'Disconnected': 'bg-gray-100 text-gray-800 hover:bg-gray-200'
    };
    return (
      <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'ERP/Procurement':
        return <Database className="h-5 w-5 text-blue-500" />;
      case 'Invoice System':
        return <CreditCard className="h-5 w-5 text-green-500" />;
      case 'CRM/Sales Data':
        return <Users className="h-5 w-5 text-purple-500" />;
      case 'Media Channel':
        return <Tv className="h-5 w-5 text-orange-500" />;
      case 'Data Connector':
        return <Globe className="h-5 w-5 text-cyan-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getEventImpactBadge = (impact) => {
    const impactColors = {
      'Very High': 'bg-red-100 text-red-800',
      'High': 'bg-orange-100 text-orange-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800'
    };
    return <Badge className={impactColors[impact]}>{impact}</Badge>;
  };

  const filteredIntegrations = allIntegrations.filter(integration => {
    const matchesSearch = 
      integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      integration.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      integration.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilters = 
      (filters.category === 'All Categories' || integration.category === filters.category) &&
      (filters.status === 'All Status' || integration.status === filters.status) &&
      (filters.region === 'All Regions' || integration.region === filters.region);
    
    return matchesSearch && matchesFilters;
  });

  const IntegrationCard = ({ integration }) => (
    <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer" onClick={() => setSelectedIntegration(integration)}>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex items-start space-x-3">
            {getCategoryIcon(integration.category)}
            <div>
              <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                {integration.name}
              </CardTitle>
              <div className="flex items-center space-x-2 mb-2">
                <Badge className="bg-blue-100 text-blue-800 text-xs">{integration.category}</Badge>
                {integration.region && (
                  <Badge className="bg-gray-100 text-gray-800 text-xs">{integration.region}</Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">{integration.vendor}</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-2">
            {getStatusBadge(integration.status)}
            {integration.healthScore !== undefined && (
              <div className={`text-sm font-semibold ${getHealthScoreColor(integration.healthScore)}`}>
                {integration.healthScore}% Health
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Data Types</div>
            <div className="flex flex-wrap gap-1">
              {integration.dataTypes && integration.dataTypes.slice(0, 3).map((type, index) => (
                <Badge key={index} className="bg-purple-100 text-purple-800 text-xs">
                  {type}
                </Badge>
              ))}
              {integration.dataTypes && integration.dataTypes.length > 3 && (
                <Badge className="bg-gray-100 text-gray-600 text-xs">
                  +{integration.dataTypes.length - 3} more
                </Badge>
              )}
              {(!integration.dataTypes || integration.dataTypes.length === 0) && (
                <Badge className="bg-gray-100 text-gray-600 text-xs">
                  No data types specified
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Sync Frequency:</span>
              <div className="font-medium">{integration.syncFrequency}</div>
            </div>
            <div>
              <span className="text-gray-500">Records:</span>
              <div className="font-medium">{integration.recordsCount?.toLocaleString() || 'N/A'}</div>
            </div>
          </div>

          {integration.lastSync && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <RefreshCw className="h-3 w-3" />
              <span>Last sync: {formatDate(integration.lastSync)}</span>
            </div>
          )}

          <div className="flex space-x-2 pt-2">
            <Button size="sm" variant="outline" className="flex-1">
              <Settings className="h-4 w-4 mr-1" />
              Configure
            </Button>
            <Button size="sm" variant="outline">
              {integration.status === 'Connected' || integration.status === 'Active' ? 
                <Wifi className="h-4 w-4" /> : 
                <WifiOff className="h-4 w-4" />
              }
            </Button>
            <Button size="sm" variant="outline">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            System Integrations & Data Connectors
          </h1>
          <p className="text-gray-600">Manage all system integrations, APIs, and regional event calendars</p>
        </div>

        <Tabs defaultValue="integrations" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="integrations" className="flex items-center">
              <Database className="h-4 w-4 mr-2" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="calendars" className="flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              Regional Calendars
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center">
              <Activity className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="integrations" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Search & Filters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="md:col-span-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search integrations, vendors..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <Select value={filters.category} onValueChange={(value) => setFilters({...filters, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {integrationCategories.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {integrationStatuses.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filters.region} onValueChange={(value) => setFilters({...filters, region: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Region" />
                    </SelectTrigger>
                    <SelectContent>
                      {integrationRegions.map(region => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-600">
                    {filteredIntegrations.length} integrations found
                  </div>
                  <div className="flex space-x-2">
                    <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      <Zap className="h-4 w-4 mr-2" />
                      Add Integration
                    </Button>
                    <Button size="sm" variant="outline">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Sync All
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredIntegrations.map((integration) => (
                <IntegrationCard key={integration.id} integration={integration} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="calendars" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regionalCalendars.map((calendar, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center">
                      <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                      {calendar.region}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {calendar.events.slice(0, 4).map((event, eventIndex) => (
                        <div key={eventIndex} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{event.name}</div>
                            <div className="text-sm text-gray-600">
                              {new Date(event.date).toLocaleDateString()}
                              {event.duration && ` (${event.duration} days)`}
                            </div>
                            <Badge className="bg-blue-100 text-blue-800 text-xs mt-1">
                              {event.type}
                            </Badge>
                          </div>
                          <div>
                            {getEventImpactBadge(event.impact)}
                          </div>
                        </div>
                      ))}
                      {calendar.events.length > 4 && (
                        <Button variant="ghost" size="sm" className="w-full">
                          View {calendar.events.length - 4} more events
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Total Integrations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">{allIntegrations.length}</div>
                  <p className="text-sm text-gray-600 mt-1">
                    {allIntegrations.filter(i => i.status === 'Connected' || i.status === 'Active').length} active
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Data Records</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {allIntegrations.reduce((acc, i) => acc + (i.recordsCount || 0), 0).toLocaleString()}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Total records synced</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Average Health</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600">
                    {Math.round(allIntegrations.reduce((acc, i) => acc + (i.healthScore || 0), 0) / allIntegrations.length)}%
                  </div>
                  <p className="text-sm text-gray-600 mt-1">System health score</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Regions Covered</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-600">
                    {new Set(allIntegrations.filter(i => i.region && i.region !== 'All Regions').map(i => i.region)).size}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">MENA regions</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Integrations;